import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import CategoryFilterRow from "../CategoryFilterRow";
import FilterChip from "./FilterChip";
import LocationFilterDialog from "./LocationFilterDialog";
import {
  countryOptions,
  deliveryMethodOptions,
  paymentMethodOptions,
  paymentTimingFilterOptions,
} from "../../utility/constants";

// Same timing as CategoryFilterRow / the mobile filter panel.
const DURATION_MS = 300;
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

const deliveryMethodIcons = {
  PICKUP: StorefrontOutlinedIcon,
  DELIVERY: LocalShippingOutlinedIcon,
  SERVICE: WorkOutlineOutlinedIcon,
};

const paymentMethodIcons = {
  CASH_ON_DELIVERY: PaymentsOutlinedIcon,
  BANK_TRANSFER: AccountBalanceOutlinedIcon,
  MOBILE_MONEY: PhoneAndroidOutlinedIcon,
  VENMO: AccountBalanceWalletOutlinedIcon,
  ZELLE: SwapHorizOutlinedIcon,
};

const paymentTimingIcons = {
  UPFRONT: BoltOutlinedIcon,
  ON_PICKUP: ShoppingBagOutlinedIcon,
  ON_DELIVERY: DeliveryDiningOutlinedIcon,
};

const ChipDivider = () => (
  <Box sx={{ width: "1px", bgcolor: "#e5e7eb", mx: 0.5 }} />
);

// Filter block shared by SearchPage and the home feed. Two rows of category
// chips are always visible; the "More filters" header expands them to a taller
// grid and reveals sort (optional), location, and delivery + payment method +
// payment timing. `filters` is the object returned by hooks/useCatalogFilters.
// Omit `sortOptions` to hide the sort row (e.g. SearchPage's People tab).
// Mirrors ngwaahia-mobile's components/filters/FilterPanel.js.
const FilterPanel = ({
  filters,
  sortOptions,
  sortValue,
  onSortChange,
  // The sort row has no "unset" state in search, but the feed's default is no
  // sort — let it count toward the header's active indicator.
  hasActiveSort = false,
  onClearAll,
  isFiltering = false,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  const {
    selectedCategories,
    selectedDeliveryMethods,
    selectedPaymentMethods,
    selectedPaymentTimings,
    locationCountry,
    locationState,
    hasLocation,
    hasActiveFilters,
  } = filters;
  const showActive = hasActiveFilters || hasActiveSort;

  return (
    <Box>
      {/* Header toggle */}
      <Box
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          py: 1,
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            variant="caption"
            sx={{ color: "#9ca3af", fontWeight: 600, letterSpacing: 0.3 }}
          >
            {open ? "FEWER FILTERS" : "MORE FILTERS"}
          </Typography>
          {showActive && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#046287",
              }}
            />
          )}
          {isFiltering && (
            <CircularProgress size={14} sx={{ color: "#046287" }} />
          )}
          {showActive && onClearAll && (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClearAll();
              }}
              sx={{
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                p: 0,
                color: "#046287",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Clear all
            </Box>
          )}
        </Box>
        <ExpandMoreIcon
          sx={{
            color: "#9ca3af",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: `transform ${DURATION_MS}ms ${EASING}`,
          }}
          fontSize="small"
        />
      </Box>

      {/* Categories: always visible, taller grid when open */}
      <CategoryFilterRow
        selectedCategories={selectedCategories}
        onToggleCategory={filters.toggleCategory}
        expanded={open}
      />

      <Collapse in={open} timeout={DURATION_MS} easing={EASING}>
        <Box sx={{ pb: 1 }}>
          {/* Sort */}
          {!!sortOptions && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {sortOptions.map((opt) => {
                  const selected = sortValue === opt.value;
                  return (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      size="small"
                      variant="outlined"
                      onClick={() => onSortChange(opt.value)}
                      sx={{
                        cursor: "pointer",
                        borderColor: selected ? "#046287" : undefined,
                        color: selected ? "#046287" : undefined,
                        fontWeight: selected ? 700 : 400,
                      }}
                    />
                  );
                })}
              </Box>
            </>
          )}

          {/* Location */}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Chip
              icon={<LocationOnOutlinedIcon sx={{ fontSize: 16 }} />}
              label={
                locationState
                  ? locationState
                  : locationCountry
                    ? countryOptions.find((c) => c.value === locationCountry)
                        ?.label
                    : "Location"
              }
              size="small"
              onClick={() => setLocationDialogOpen(true)}
              sx={{
                cursor: "pointer",
                bgcolor: hasLocation ? "#046287" : undefined,
                color: hasLocation ? "white" : undefined,
                "& .MuiChip-icon": { color: hasLocation ? "white" : undefined },
                "&:hover": { bgcolor: hasLocation ? "#046287" : undefined },
              }}
            />
            {hasLocation && (
              <IconButton
                size="small"
                onClick={filters.clearLocation}
                aria-label="Clear location filter"
                sx={{ p: 0.25 }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

          {/* Delivery + payment method + payment timing */}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {deliveryMethodOptions.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                icon={deliveryMethodIcons[opt.value]}
                selected={selectedDeliveryMethods.includes(opt.value)}
                onClick={() => filters.toggleDeliveryMethod(opt.value)}
              />
            ))}
            <ChipDivider />
            {paymentMethodOptions.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                icon={paymentMethodIcons[opt.value]}
                selected={selectedPaymentMethods.includes(opt.value)}
                onClick={() => filters.togglePaymentMethod(opt.value)}
              />
            ))}
            <ChipDivider />
            {paymentTimingFilterOptions.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                icon={paymentTimingIcons[opt.value]}
                selected={selectedPaymentTimings.includes(opt.value)}
                onClick={() => filters.togglePaymentTiming(opt.value)}
              />
            ))}
          </Box>
        </Box>
      </Collapse>

      <LocationFilterDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        initialCountry={locationCountry}
        initialState={locationState}
        onApply={filters.setLocation}
      />
    </Box>
  );
};

export default FilterPanel;
